import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList, FileText, ChevronDown, ChevronUp, ChevronRight, Search,
} from 'lucide-react';
import StatCard from '../shared/StatCard';
import StatusFilterDropdown from '../shared/StatusFilterDropdown';
import DateRangeFilter from '../shared/DateRangeFilter';
import ProjectFilter from '../shared/ProjectFilter';
import { NAVY, BORDER_COLOR, bubbleStyle, selectStyle } from '../../styles/tokens';
import { STATUS_COLORS, STATUS_LABEL, PAYMENT_METHODS } from '../../../../shared/constants';
import client from '../../api/client';
import AuthedReceiptPreview from '../../shared/components/AuthedReceiptPreview';

const FILTERS = [
  { value: 'all',                  label: 'Todos' },
  { value: 'pending',              label: 'Pendiente' },
  { value: 'partially_approved',   label: 'Parcial Aprobado' },
  { value: 'sent_back',            label: 'Devuelto' },
  { value: 'partially_returned',   label: 'Parcial Devuelto' },
  { value: 'pending_review',       label: 'Pend. Revisión' },
  { value: 'approved',             label: 'Aprobado' },
  { value: 'paid',                 label: 'Pagado' },
  { value: 'rejected',             label: 'Rechazado' },
];

const STATUS_ORDER = ['pending', 'partially_approved', 'sent_back', 'partially_returned', 'pending_review', 'approved', 'paid', 'rejected'];

const SECTION_LABELS = {
  pending:              'En Revisión',
  partially_approved:   'Parcialmente Aprobado',
  sent_back:            'Devuelto',
  partially_returned:   'Parcialmente Devuelto',
  pending_review:       'Pendiente de Revisión',
  approved:             'Aprobado',
  paid:                 'Pagado',
  rejected:             'Rechazado',
};

function fmt(amount) {
  if (amount === null || amount === undefined) return '—';
  return '$' + parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: '600',
      whiteSpace: 'nowrap',
    }}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function SectionHeader({ status, count, expanded, onToggle }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const Icon = expanded ? ChevronUp : ChevronDown;
  return (
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        background: '#fff',
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: expanded ? '12px 12px 0 0' : 12,
        cursor: 'pointer',
        fontFamily: 'Inter, sans-serif',
        minHeight: 48,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
        <span style={{
          width: 10, height: 10, borderRadius: '50%',
          background: s.border, flexShrink: 0, display: 'inline-block',
        }} />
        <span style={{ fontWeight: '600', fontSize: 14, color: NAVY }}>
          {SECTION_LABELS[status] || status}
        </span>
      </div>
      <span style={{
        background: '#f1f5f9', color: '#64748b',
        borderRadius: 20, padding: '2px 10px',
        fontSize: 12, fontWeight: '500',
        marginRight: 12,
      }}>
        {count} recibo{count !== 1 ? 's' : ''}
      </span>
      <Icon size={16} style={{ color: '#94a3b8', flexShrink: 0 }} />
    </button>
  );
}

export default function AdminAllReceipts({ employees }) {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(new Set());
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [employeeId, setEmployeeId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [sectionExpanded, setSectionExpanded] = useState({
    pending: true, partially_approved: true, sent_back: true,
    partially_returned: true, approved: false, rejected: false,
  });

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (employeeId) params.employeeId = employeeId;
      const res = await client.get('/api/receipts', { params });
      setReceipts(res.data);
    } catch (err) {
      console.error('Failed to load receipts:', err);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, employeeId]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  // Derive unique project names from all receipts
  const allProjectNames = useMemo(() => {
    const names = new Set();
    receipts.forEach(r => (r.items || []).forEach(i => { if (i.projectName) names.add(i.projectName); }));
    return [...names].sort();
  }, [receipts]);

  // Apply status filter, search, and project filter
  const filtered = useMemo(() => {
    return receipts.filter(r => {
      if (filter.size > 0) {
        const match = filter.has(r.status)
          || (filter.has('pending') && r.status === 'partially_approved')
          || (filter.has('returned') && r.status === 'partially_returned')
          || (filter.has('returned') && r.status === 'sent_back');
        if (!match) return false;
      }
      if (selectedProjects.length > 0) {
        const hasMatch = (r.items || []).some(i => selectedProjects.includes(i.projectName));
        if (!hasMatch) return false;
      }
      if (searchText) {
        const q = searchText.toLowerCase();
        const match = (r.vendor || '').toLowerCase().includes(q)
          || (r.employeeName || '').toLowerCase().includes(q)
          || (r.receiptNumber || '').toString().includes(q)
          || (r.items || []).some(i =>
            (i.description || '').toLowerCase().includes(q)
            || (i.projectName || '').toLowerCase().includes(q)
            || (i.supervisorName || '').toLowerCase().includes(q)
          );
        if (!match) return false;
      }
      return true;
    });
  }, [receipts, filter, searchText, selectedProjects]);

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    setDateFrom(df);
    setDateTo(dt);
  }

  function toggleSection(status) {
    setSectionExpanded(prev => ({ ...prev, [status]: !prev[status] }));
  }

  // Stats
  const totalItems = (r) => (r.items || []).length;
  const totalAmount = (r) => {
    const items = r.items || [];
    if (items.length > 0) return items.reduce((s, i) => { const a=parseFloat(i.amount)||0; return s+a+a*(parseFloat(i.taxRate)||0)/100; }, 0);
    return r.amount || 0;
  };

  const pending = filtered.filter(r => r.status === 'pending');
  const sentBack = filtered.filter(r => r.status === 'sent_back' || r.status === 'partially_returned');
  const rejected = filtered.filter(r => r.status === 'rejected');
  const approved = filtered.filter(r => r.status === 'approved');
  const approvedAmt = approved.reduce((s, r) => s + (totalAmount(r) || 0), 0);

  const renderReceiptCard = (r) => {
    const items = r.items || [];
    const projectNames = [...new Set(items.map(i => i.projectName).filter(Boolean))];
    const supervisorNames = [...new Set(items.map(i => i.supervisorName).filter(Boolean))];

    return (
      <div
        key={r.id}
        onClick={() => navigate(`/admin/receipt/${r.id}`)}
        style={{
          ...bubbleStyle,
          padding: '14px 18px',
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, transform 0.1s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = '';
          e.currentTarget.style.transform = '';
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          {/* Preview thumbnail */}
          <div style={{
            width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
            flexShrink: 0, background: '#f1f5f9', border: `1px solid ${BORDER_COLOR}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {r.previewUrl && r.fileType?.startsWith('image/') ? (
              <AuthedReceiptPreview src={r.previewUrl} fileType={r.fileType} alt="receipt" imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} style={{ width: '100%', height: '100%' }} />
            ) : (
              <FileText size={20} style={{ color: '#94a3b8' }} />
            )}
          </div>

          {/* Receipt info */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '700', fontSize: 15, color: '#1e293b' }}>
                {r.receiptNumber ? `#${r.receiptNumber}` : '(Sin #)'}
                {' — '}
                {r.vendor || '(Sin proveedor)'}
              </span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>{r.date || '—'}</span>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>|</span>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {r.paymentMethod ? (PAYMENT_METHODS[r.paymentMethod] || r.paymentMethod) : '—'}
              </span>
              <StatusBadge status={r.displayStatus || r.status} />
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {r.employeeName && <span style={{ marginRight: 8 }}>{r.employeeName}</span>}
              {supervisorNames.length > 0 && <span style={{ marginRight: 8 }}>| Supervisores: {supervisorNames.join(', ')}</span>}
              {projectNames.length > 0 && <span>| Proyectos: {projectNames.join(', ')}</span>}
            </div>
            {r.reviewerName && r.status === 'approved' && supervisorNames.length > 0
              && !supervisorNames.includes(r.reviewerName) && (
              <div style={{
                fontSize: 11, color: '#0369a1', background: '#e0f2fe',
                padding: '2px 8px', borderRadius: 6, marginTop: 3,
                display: 'inline-block',
              }}>
                Aprobado por Administrador: {r.reviewerName}
              </div>
            )}
          </div>

          {/* Total + chevron */}
          <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: '700', fontSize: 18, color: '#1e293b' }}>{fmt(totalAmount(r))}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{totalItems(r)} item{totalItems(r) !== 1 ? 's' : ''}</div>
            </div>
            <ChevronRight size={18} style={{ color: '#94a3b8' }} />
          </div>
        </div>
      </div>
    );
  };

  // Grouped view (filter === 'all')
  const renderGroupedView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {STATUS_ORDER.map(status => {
        const sectionReceipts = filtered.filter(r => r.status === status);
        if (sectionReceipts.length === 0) return null;
        const isExpanded = sectionExpanded[status];
        return (
          <div key={status}>
            <SectionHeader
              status={status}
              count={sectionReceipts.length}
              expanded={isExpanded}
              onToggle={() => toggleSection(status)}
            />
            {isExpanded && (
              <div style={{
                border: `1px solid ${BORDER_COLOR}`,
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                overflow: 'hidden',
                background: '#f8fafc',
                display: 'flex', flexDirection: 'column', gap: 12,
                padding: 12,
              }}>
                {sectionReceipts.map(r => renderReceiptCard(r))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Flat view (specific filter)
  const renderFlatView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {filtered.map(r => renderReceiptCard(r))}
    </div>
  );

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto', animation: 'fadeUp 0.3s ease' }}>
      <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: '0 0 18px' }}>
        Todos los Recibos
      </h2>

      {/* Stats */}
      <div className="stat-grid" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Total" value={filtered.length} onClick={() => setFilter(new Set())} active={filter.size === 0} />
        <StatCard label="Pendiente" value={pending.length} onClick={() => setFilter(new Set(['pending']))} active={filter.size === 1 && filter.has('pending')} />
        <StatCard label="Devuelto" value={sentBack.length} onClick={() => setFilter(new Set(['sent_back', 'partially_returned']))} active={filter.has('sent_back') || filter.has('partially_returned')} />
        <StatCard label="Aprobado" value={approved.length} subtitle={fmt(approvedAmt)} subtitleLarge highlight
          onClick={() => setFilter(new Set(['approved']))} active={filter.size === 1 && filter.has('approved')} />
        <StatCard label="Rechazado" value={rejected.length} onClick={() => setFilter(new Set(['rejected']))} active={filter.size === 1 && filter.has('rejected')} />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <StatusFilterDropdown options={FILTERS} active={filter} onChange={setFilter} />
        <div className="date-range" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} onChange={handleDateChange} />
        </div>
        {employees && employees.length > 0 && (
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            style={{ ...selectStyle, width: 'auto', minWidth: 160, height: 34, paddingTop: 0, paddingBottom: 0, fontSize: 13 }}
          >
            <option value="">Todos los Choferes</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
        <ProjectFilter projects={allProjectNames} selected={selectedProjects} onChange={setSelectedProjects} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, color: '#94a3b8' }} />
          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Buscar..."
            style={{
              ...selectStyle, width: 200, paddingLeft: 30,
              height: 34, paddingTop: 0, paddingBottom: 0, fontSize: 13,
            }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 14 }}>Cargando recibos...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <ClipboardList size={44} style={{ marginBottom: 12, color: '#cbd5e1' }} />
          <div style={{ fontSize: 16, fontWeight: '600', color: '#64748b' }}>No hay recibos</div>
        </div>
      ) : filter.size === 0 ? renderGroupedView() : renderFlatView()}
    </div>
  );
}
